import type { RefObject } from 'react'
import { Link } from 'react-router'
import { CopyButton } from '@/components/patterns/CopyButton'
import { QuickActions, type QuickAction, type QuickMenuItem } from '@/components/patterns/QuickActions'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { CategoryChip } from '../components/CategoryChip'
import { PhoneLink } from '../record/PhoneLink'
import { LabelChip, type LabelLike } from '../components/LabelChip'
import { LabelPicker } from '../components/LabelPicker'
import type { CrmContactDetail } from '../types'

/**
 * The left column's head of the contact page (design §4.5): avatar and name, "Functie bij
 * <organisatie>", category, labels, e-mail and phone with a copy button, and the quick actions.
 * The labels are the page's: they show what was just clicked while the answer is on its way.
 */
export function ContactHeader({
  detail,
  labels,
  onLabelsChange,
  actions,
  more,
  actionsRef,
}: {
  detail: CrmContactDetail
  labels: LabelLike[]
  onLabelsChange: (labels: LabelLike[]) => void
  actions: QuickAction[]
  more: QuickMenuItem[]
  /** The element around the quick actions, for putting the focus back on "Meer". */
  actionsRef?: RefObject<HTMLDivElement | null>
}) {
  const primary = detail.organisations.find((membership) => membership.is_primary && !membership.ended_on)
  const archived = detail.status === 'inactief'
  return (
    <>
      <div className="flex items-start gap-3">
        <EntityAvatar kind="person" name={detail.full_name} size="xl" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="font-heading text-xl font-semibold break-words">{detail.full_name}</h1>
          {primary ? (
            <p className="text-sm text-muted-foreground">
              {detail.job_title ? `${detail.job_title} bij ` : null}
              <Link
                to={`/relaties/organisaties/${primary.organisation_id}`}
                className="text-foreground underline-offset-2 hover:underline"
              >
                {primary.organisation_name}
              </Link>
            </p>
          ) : detail.job_title ? (
            <p className="text-sm text-muted-foreground">{detail.job_title}</p>
          ) : null}
          {archived ? <p className="text-xs font-medium text-muted-foreground">Gearchiveerd</p> : null}
        </div>
      </div>
      {detail.category ? <CategoryChip category={detail.category} size="md" /> : null}
      <div role="group" aria-label="Labels" className="flex flex-wrap items-center gap-1">
        {labels.map((label) => (
          <LabelChip
            key={label.id}
            label={label}
            onRemove={() => onLabelsChange(labels.filter((item) => item.id !== label.id))}
          />
        ))}
        <LabelPicker selected={labels} onChange={onLabelsChange} />
      </div>
      <div className="flex flex-col gap-1 text-sm">
        {detail.email ? (
          <p className="flex items-center gap-1">
            <span className="min-w-0 break-all">{detail.email}</span>
            <CopyButton value={detail.email} label="E-mailadres kopiëren" />
          </p>
        ) : null}
        {detail.phone ? (
          <p className="flex items-center gap-1">
            <PhoneLink
              phone={detail.phone}
              relation={{ type: 'contact', id: detail.person_id, name: detail.full_name }}
            />
          </p>
        ) : (
          // Editing happens in "Over dit contact".
          <p className="text-muted-foreground">Telefoon toevoegen…</p>
        )}
      </div>
      <div ref={actionsRef}>
        <QuickActions actions={actions} more={more} />
      </div>
    </>
  )
}
